/**
 * Per-agent mascot tones — the data table behind "one character per agent".
 *
 * Eight light tints around the hue wheel. Every pair is at least ΔE2000 12
 * apart, so two agents never look alike at list size (tested). No grey: a grey
 * face reads as "disabled", and paused is already said by the asleep mood.
 * Order is part of the contract — `toneForAgent` indexes into it, so appending
 * is safe and reordering re-colours every existing agent.
 *
 * The brand face (Blueticks AI, the builder) is NOT in this table: it is the
 * white/ink head from the `--ai-mascot-head*` tokens, so a customer agent can
 * never look like it.
 */
export const AGENT_TONES = [
  '#f7cad9', // rose
  '#bfe3ff', // sky
  '#c9f7de', // mint
  '#d1d0f9', // periwinkle
  '#ffddb5', // apricot
  '#f4f7af', // lemon
  '#ff9d9a', // coral
  '#ffb1f9', // orchid
] as const;

export type AgentTone = (typeof AGENT_TONES)[number];

/**
 * Deterministic tone for an agent id (FNV-1a over the whole id). Same id → same
 * tone on every device; no schema field.
 *
 * The bucket comes from the hash's HIGH bits. An FNV-1a hash's low k bits depend
 * only on the low k bits of each character, so `hash % 8` sorts ObjectIds (hex
 * digits, a timestamp prefix) into far fewer buckets than 8 — 9 staging agents
 * landed on 4 tones that way.
 */
export function toneForAgent(agentId: string): AgentTone {
  let hash = 0x811c9dc5;
  for (let i = 0; i < agentId.length; i++) {
    hash ^= agentId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return AGENT_TONES[Math.floor(((hash >>> 0) / 2 ** 32) * AGENT_TONES.length)];
}
